import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { catchError, finalize, forkJoin, mapTo, Observable, of, shareReplay, tap } from 'rxjs';
import { ActivityCategory, ActivityType, ActivityTypeKind, ScheduleType } from '../activities/activity-type.model';
import { ActivityTypeService } from '../activities/activity-type.service';
import { describeGenericApiError, extractApiErrorMessage } from '../../core/http/api-error.util';
import { ConfirmDialogService } from '../../core/ui/confirm-dialog.service';
import { generateIdempotencyKey } from '../../core/utils/idempotency-key.util';
import { HabitIncrementResponse } from '../habits/habit.model';
import { HabitService } from '../habits/habit.service';
import { DayStatusCycleKind, LastCompletedCycleSummary, ScheduleGroup } from '../settings/me.model';
import { MeService } from '../settings/me.service';
import { DayBlock, DayHabit, DayViewResponse, PutDayAllocationInput, PutDayAllocationsRequest } from './day.model';
import { DayService } from './day.service';
import {
  DayStatusCycles,
  computeCycleDurationDays,
  computeDayStatusCycles,
  computeDaysSince,
  findCycleCompletedBySettingAside,
  formatDaysSince,
  isDoneThisCycle,
  resolveScheduleGroupCycle
} from './day-status-cycles.util';
import { DayStatus, computeDayStatus } from './day-status.util';
import { habitDisplaySortRows } from './habit-sort.util';
import {
  SCHEDULE_GROUP_ORDER,
  buildScheduleGroupPositionUpdates,
  getEnabledScheduleGroups
} from './schedule-group.util';
import { StartTimeBlockRequest, TimeBlockResponse, TimeBlockSource } from './time-block.model';
import { TimeBlockService } from './time-block.service';

export type AllocationRow = {
  activityTypeId: string;
  name: string;
  completedSlots: number;
  currentSlots: number;
  isCompleted: boolean;
  startDurationMinutes: number | null;
  category: ActivityCategory;
  scheduleType: ScheduleType;
  loggedMinutes: number;
  isHidden: boolean;
  focusedTaskCount: number;
  prioritizedTaskCount: number;
  hasFocusToday: boolean;
  hasPriorityToday: boolean;
  daysSince: number | null;
  daysSinceLabel: string;
  doneThisCycle: boolean;
  notThisCycle: boolean;
};

@Injectable({ providedIn: 'root' })
export class DayStore {
  private readonly activityTypeService = inject(ActivityTypeService);
  private readonly dayService = inject(DayService);
  private readonly habitService = inject(HabitService, { optional: true });
  private readonly meService = inject(MeService, { optional: true });
  private readonly timeBlockService = inject(TimeBlockService, { optional: true });
  private readonly router = inject(Router);
  private readonly confirmDialog = inject(ConfirmDialogService);

  readonly activityTypes = signal<ActivityType[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly day = signal<DayViewResponse | null>(null);
  readonly dayLoading = signal(false);
  readonly dayError = signal<string | null>(null);
  readonly habitSubmitting = signal<Record<string, boolean>>({});
  readonly startSubmitting = signal<Record<string, boolean>>({});
  readonly startIdempotencyKeys = signal<Record<string, string>>({});
  readonly blockMutationSubmitting = signal(false);
  readonly blockMutationError = signal<string | null>(null);
  readonly completeMutationState = signal<{ blockId: string; idempotencyKey: string } | null>(null);
  readonly skipMutationState = signal<{ blockId: string; idempotencyKey: string } | null>(null);
  readonly pauseMutationState = signal<{ blockId: string; idempotencyKey: string } | null>(null);
  readonly resumeMutationState = signal<{ blockId: string; idempotencyKey: string } | null>(null);
  readonly allocationSaving = signal(false);
  readonly allocationSaveError = signal<string | null>(null);
  readonly allocationSaveSuccess = signal<string | null>(null);
  readonly reorderSubmitting = signal(false);
  readonly activityReorderSubmitting = signal(false);
  readonly activityReorderError = signal<string | null>(null);
  readonly notThisCycleSubmitting = signal<Record<string, boolean>>({});
  readonly notThisCycleError = signal<string | null>(null);
  readonly scheduleGroups = signal<ScheduleGroup[]>(this.defaultScheduleGroups());
  readonly timezone = signal('UTC');
  readonly workCycleStartedAt = signal<string | null>(null);
  readonly lifeCycleStartedAt = signal<string | null>(null);
  readonly workCycleLabel = signal<string | null>(null);
  readonly lifeCycleLabel = signal<string | null>(null);
  readonly workCycleCompletedCount = signal(0);
  readonly lifeCycleCompletedCount = signal(0);
  readonly lastCompletedWorkCycle = signal<LastCompletedCycleSummary | null>(null);
  readonly lastCompletedLifeCycle = signal<LastCompletedCycleSummary | null>(null);
  readonly dayStatusOldestFirst = signal(false);
  readonly celebrationCycle = signal<DayStatusCycleKind | null>(null);
  readonly celebrationSummary = signal<LastCompletedCycleSummary | null>(null);
  readonly effectiveDefaultSlotMinutes = signal<number | null>(null);
  readonly defaultSlotMinutesLoading = signal(false);
  readonly defaultSlotMinutesError = signal<string | null>(null);
  readonly hiddenActivityIds = signal<Set<string>>(new Set());
  readonly nowMs = signal(Date.now());

  readonly habitRows = computed<Array<DayHabit & {
    name: string;
    isCompleted: boolean;
    openTaskCount: number;
    hasOpenTasks: boolean;
    displayOrder: number;
    daysSince: number | null;
    daysSinceLabel: string;
  }>>(() => {
    const lastDoneById = new Map(this.activityTypes().map((activity) => [activity.id, activity.lastDoneAt]));
    const nameById = new Map(this.activityTypes().map((activity) => [activity.id, activity.name]));
    const nowMs = this.nowMs();
    const timeZone = this.timezone();
    return habitDisplaySortRows(this.activityTypes(), this.day()?.habits).map((row) => {
      const daysSince = computeDaysSince(lastDoneById.get(row.activityTypeId), nowMs, timeZone);
      return {
        ...row,
        name: nameById.get(row.activityTypeId) ?? '',
        isCompleted: row.target !== null && row.total >= row.target,
        hasOpenTasks: row.openTaskCount > 0,
        daysSince,
        daysSinceLabel: formatDaysSince(daysSince)
      };
    });
  });

  readonly enabledScheduleGroups = computed(() => getEnabledScheduleGroups(this.scheduleGroups()));
  readonly dayStatus = computed<DayStatus | null>(() => {
    const currentDay = this.day();
    if (!currentDay) {
      return null;
    }
    return computeDayStatus(
      currentDay.settings.dayWindowStartLocalTime,
      currentDay.settings.dayWindowEndLocalTime,
      currentDay.allocations,
      this.scheduleGroups(),
      this.nowMs(),
      [currentDay.runningBlock, ...currentDay.pausedBlocks].filter((block): block is DayBlock => block !== null)
    );
  });

  readonly dayStatusCycles = computed<DayStatusCycles>(() => computeDayStatusCycles(
    this.scheduleGroups(),
    this.activityTypes(),
    this.workCycleStartedAt(),
    this.lifeCycleStartedAt(),
    this.nowMs(),
    this.timezone(),
    this.workCycleLabel(),
    this.lifeCycleLabel()
  ));

  private cacheStale = true;
  private hasLoadedDay = false;
  private hasLoadedActivityTypes = false;
  private hasLoadedDefaultSlotMinutes = false;
  private ensureLoadedRequest$: Observable<void> | null = null;
  private shellActivityTypesRequested = false;
  private shellSettingsRequested = false;
  private refreshRequest$: Observable<void> | null = null;
  private refreshSettledCallbacks: Array<() => void> = [];
  private celebrationTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private nowMsIntervalId: ReturnType<typeof setInterval> | null = null;

  constructor() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd && !event.urlAfterRedirects.startsWith('/today')) {
        this.cacheStale = true;
      }
    });
    this.startNowMsTicker();
  }

  hasFreshDayData(): boolean {
    return !this.cacheStale && this.hasLoadedDay;
  }

  ensureLoaded(): Observable<void> {
    if (
      !this.cacheStale
      && this.hasLoadedDay
      && this.hasLoadedActivityTypes
      && this.hasLoadedDefaultSlotMinutes
    ) {
      return of(void 0);
    }

    if (this.ensureLoadedRequest$) {
      return this.ensureLoadedRequest$;
    }

    if (this.cacheStale) {
      this.day.set(null);
    }

    this.ensureLoadedRequest$ = forkJoin({
      activityTypesLoaded: this.loadActivityTypes$(),
      defaultSlotMinutesLoaded: this.loadDefaultSlotMinutes$(),
      dayLoaded: this.loadToday$({ silent: false })
    }).pipe(
      tap((result) => {
        if (this.cacheStale) {
          this.hasLoadedActivityTypes = result.activityTypesLoaded;
          this.hasLoadedDefaultSlotMinutes = result.defaultSlotMinutesLoaded;
          this.hasLoadedDay = result.dayLoaded;
        } else {
          this.hasLoadedActivityTypes = this.hasLoadedActivityTypes || result.activityTypesLoaded;
          this.hasLoadedDefaultSlotMinutes = this.hasLoadedDefaultSlotMinutes || result.defaultSlotMinutesLoaded;
          this.hasLoadedDay = this.hasLoadedDay || result.dayLoaded;
        }
        this.cacheStale = !(
          this.hasLoadedActivityTypes
          && this.hasLoadedDefaultSlotMinutes
          && this.hasLoadedDay
        );
      }),
      mapTo(void 0),
      finalize(() => {
        this.ensureLoadedRequest$ = null;
      }),
      shareReplay(1)
    );

    return this.ensureLoadedRequest$;
  }

  refresh(options?: { silent?: boolean; onSettled?: () => void }): Observable<void> {
    if (options?.onSettled) {
      this.refreshSettledCallbacks.push(options.onSettled);
    }

    if (this.refreshRequest$) {
      return this.refreshRequest$;
    }

    this.refreshRequest$ = this.loadToday$({ silent: options?.silent }).pipe(
      tap((loaded) => {
        this.hasLoadedDay = this.hasLoadedDay || loaded;
      }),
      mapTo(void 0),
      finalize(() => {
        const callbacks = [...this.refreshSettledCallbacks];
        this.refreshSettledCallbacks = [];
        for (const callback of callbacks) {
          callback();
        }
        this.refreshRequest$ = null;
      }),
      shareReplay(1)
    );

    return this.refreshRequest$;
  }

  /**
   * Light load for app-shell consumers such as the nav-bar timer pill on pages that don't load the
   * day themselves (Focus, Activities, Settings, detail pages). Refreshes today's day view silently,
   * and loads activity types and group settings once if nothing has loaded them yet. It never clears
   * the day or flips the loading states the Today page relies on.
   */
  refreshForShell(): void {
    if (!this.hasLoadedActivityTypes && !this.shellActivityTypesRequested) {
      this.shellActivityTypesRequested = true;
      this.loadActivityTypes$().subscribe((loaded) => {
        this.hasLoadedActivityTypes = this.hasLoadedActivityTypes || loaded;
        this.shellActivityTypesRequested = false;
      });
    }
    if (!this.hasLoadedDefaultSlotMinutes && !this.shellSettingsRequested) {
      this.shellSettingsRequested = true;
      this.loadDefaultSlotMinutes$().subscribe((loaded) => {
        this.hasLoadedDefaultSlotMinutes = this.hasLoadedDefaultSlotMinutes || loaded;
        this.shellSettingsRequested = false;
      });
    }
    this.refresh({ silent: true }).subscribe();
  }

  load(): void {
    this.loadActivityTypes$().subscribe((loaded) => {
      this.hasLoadedActivityTypes = this.hasLoadedActivityTypes || loaded;
    });
  }

  loadToday(options?: { silent?: boolean; onSettled?: () => void }): void {
    this.refresh(options).subscribe();
  }

  retryLoadDefaultSlotMinutes(): void {
    this.loadDefaultSlotMinutes$().subscribe((loaded) => {
      this.hasLoadedDefaultSlotMinutes = this.hasLoadedDefaultSlotMinutes || loaded;
    });
  }

  refreshDayError(): void {
    this.refresh().subscribe();
  }

  refreshAllocationError(): void {
    this.allocationSaveError.set(null);
    this.allocationSaveSuccess.set(null);
    this.activityReorderError.set(null);
    this.refresh().subscribe();
  }

  refreshTimerError(onSettled?: () => void): void {
    this.refresh({ onSettled }).subscribe();
  }

  incrementHabit(activityTypeId: string): void {
    const day = this.day();
    const habitService = this.habitService;
    if (!day || !habitService) return;
    this.setHabitSubmitting(activityTypeId, true);
    habitService.increment(activityTypeId, { dayDate: day.dayDate, increment: 1 }).subscribe({
      next: (response) => {
        this.applyHabitIncrement(response);
        this.setHabitSubmitting(activityTypeId, false);
      },
      error: (err: unknown) => {
        this.dayError.set(describeGenericApiError(err, 'Failed to increment habit'));
        this.setHabitSubmitting(activityTypeId, false);
      }
    });
  }

  startBlock(activityTypeId: string): void {
    const currentDay = this.day();
    const timeBlockService = this.timeBlockService;
    if (!currentDay || !timeBlockService || currentDay.runningBlock || this.hasAnyStartSubmitting()) return;
    if (this.getPausedBlockForActivity(activityTypeId)) {
      this.dayError.set('A paused block already exists for this activity. Resume it instead.');
      return;
    }

    const durationMinutes = this.resolveStartDurationForActivity(activityTypeId);
    if (durationMinutes === null || durationMinutes <= 0) {
      this.dayError.set('Cannot start block: duration minutes could not be resolved.');
      return;
    }

    const request: StartTimeBlockRequest = {
      dayDate: currentDay.dayDate,
      activityTypeId,
      durationMinutes,
      source: this.resolveStartSourceForActivity(activityTypeId, currentDay)
    };

    this.setStartSubmitting(activityTypeId, true);
    this.dayError.set(null);
    this.blockMutationError.set(null);
    const idempotencyKey = this.getOrCreateStartIdempotencyKey(activityTypeId);

    timeBlockService.start(request, idempotencyKey).subscribe({
      next: () => this.refresh({ silent: true, onSettled: () => {
        this.clearStartIdempotencyKey(activityTypeId);
        this.setStartSubmitting(activityTypeId, false);
      }}).subscribe(),
      error: (err: unknown) => {
        this.dayError.set(this.describeTimeBlockMutationError(err, 'start'));
        if (err instanceof HttpErrorResponse && err.status === 409) {
          this.clearStartIdempotencyKey(activityTypeId);
          this.refresh({ silent: true, onSettled: () => this.setStartSubmitting(activityTypeId, false) }).subscribe();
          return;
        }
        this.setStartSubmitting(activityTypeId, false);
      }
    });
  }

  completeRunningBlock(): void {
    const runningBlock = this.day()?.runningBlock;
    const timeBlockService = this.timeBlockService;
    if (!runningBlock || !timeBlockService || this.blockMutationSubmitting()) return;
    this.blockMutationSubmitting.set(true);
    this.blockMutationError.set(null);
    const idempotencyKey = this.getOrCreateCompleteIdempotencyKey(runningBlock.id);

    timeBlockService.complete(runningBlock.id, { endedAt: new Date().toISOString() }, idempotencyKey).subscribe({
      next: (response) => {
        this.applyFinalizedTimeBlock(response);
        this.refresh({ silent: true, onSettled: () => {
          this.completeMutationState.set(null);
          this.blockMutationSubmitting.set(false);
        }}).subscribe();
      },
      error: (err: unknown) => {
        this.refreshDayOnFinalizeMutationError(err);
        this.blockMutationError.set(this.describeTimeBlockMutationError(err, 'complete'));
        this.blockMutationSubmitting.set(false);
      }
    });
  }

  onAutoAdvance(): void {
    const currentDay = this.day();
    const runningBlock = currentDay?.runningBlock;
    const timeBlockService = this.timeBlockService;
    if (!currentDay || !runningBlock || !timeBlockService || this.blockMutationSubmitting()) {
      return;
    }

    const allocation = currentDay.allocations.find(
      (entry) => entry.activityTypeId === runningBlock.activityTypeId
    );
    if (!allocation || allocation.slotCount <= 0) {
      return;
    }
    const remainingSlots = Math.max(0, allocation.slotCount - (allocation.completedSlots + 1));
    if (remainingSlots <= 0) {
      return;
    }

    const durationMinutes = runningBlock.durationMinutes;
    if (!durationMinutes || durationMinutes <= 0) {
      this.blockMutationError.set('Cannot auto-advance: duration minutes could not be resolved.');
      return;
    }

    const { dayDate } = currentDay;
    const { id: blockId, activityTypeId } = runningBlock;
    this.blockMutationSubmitting.set(true);
    this.blockMutationError.set(null);
    const completeIdempotencyKey = this.getOrCreateCompleteIdempotencyKey(blockId);

    timeBlockService.complete(blockId, { endedAt: new Date().toISOString() }, completeIdempotencyKey).subscribe({
      next: (completeResponse) => {
        this.applyFinalizedTimeBlock(completeResponse);

        const startRequest: StartTimeBlockRequest = {
          dayDate,
          activityTypeId,
          durationMinutes,
          source: 'ALLOCATED'
        };
        const startIdempotencyKey = this.getOrCreateStartIdempotencyKey(activityTypeId);
        timeBlockService.start(startRequest, startIdempotencyKey).subscribe({
          next: () => {
            this.refresh({ silent: true, onSettled: () => {
              this.completeMutationState.set(null);
              this.clearStartIdempotencyKey(activityTypeId);
              this.blockMutationSubmitting.set(false);
            }}).subscribe();
          },
          error: (err: unknown) => {
            this.blockMutationError.set(this.describeTimeBlockMutationError(err, 'start'));
            if (err instanceof HttpErrorResponse && err.status === 409) {
              this.clearStartIdempotencyKey(activityTypeId);
              this.refresh({ silent: true, onSettled: () => this.blockMutationSubmitting.set(false) }).subscribe();
              return;
            }
            this.blockMutationSubmitting.set(false);
          }
        });
      },
      error: (err: unknown) => {
        this.refreshDayOnFinalizeMutationError(err);
        this.blockMutationError.set(this.describeTimeBlockMutationError(err, 'complete'));
        this.blockMutationSubmitting.set(false);
      }
    });
  }

  skipRunningBlock(reason: string): void {
    const runningBlock = this.day()?.runningBlock;
    const timeBlockService = this.timeBlockService;
    if (!runningBlock || !timeBlockService || this.blockMutationSubmitting()) return;
    this.blockMutationSubmitting.set(true);
    this.blockMutationError.set(null);
    const idempotencyKey = this.getOrCreateSkipIdempotencyKey(runningBlock.id);

    timeBlockService.skip(runningBlock.id, {
      endedAt: new Date().toISOString(),
      reason: reason.trim() || 'Skipped'
    }, idempotencyKey).subscribe({
      next: (response) => {
        this.applyFinalizedTimeBlock(response);
        this.refresh({ silent: true, onSettled: () => {
          this.skipMutationState.set(null);
          this.blockMutationSubmitting.set(false);
        }}).subscribe();
      },
      error: (err: unknown) => {
        this.refreshDayOnFinalizeMutationError(err);
        this.blockMutationError.set(this.describeTimeBlockMutationError(err, 'skip'));
        this.blockMutationSubmitting.set(false);
      }
    });
  }

  pauseRunningBlock(): void {
    const runningBlock = this.day()?.runningBlock;
    const timeBlockService = this.timeBlockService;
    if (!runningBlock || !timeBlockService || runningBlock.pausedAt || this.blockMutationSubmitting()) return;
    this.blockMutationSubmitting.set(true);
    this.blockMutationError.set(null);
    const idempotencyKey = this.getOrCreatePauseIdempotencyKey(runningBlock.id);

    timeBlockService.pause(runningBlock.id, idempotencyKey).subscribe({
      next: (response) => {
        this.applyRunningTimeBlockUpdate(response);
        this.refresh({ silent: true, onSettled: () => {
          this.pauseMutationState.set(null);
          this.blockMutationSubmitting.set(false);
        }}).subscribe();
      },
      error: (err: unknown) => {
        this.refreshDayOnFinalizeMutationError(err);
        this.blockMutationError.set(this.describeTimeBlockMutationError(err, 'pause'));
        this.blockMutationSubmitting.set(false);
      }
    });
  }

  resumeRunningBlock(): void {
    const runningBlock = this.day()?.runningBlock;
    const timeBlockService = this.timeBlockService;
    if (!runningBlock || !timeBlockService || !runningBlock.pausedAt || this.blockMutationSubmitting()) return;
    this.blockMutationSubmitting.set(true);
    this.blockMutationError.set(null);
    const idempotencyKey = this.getOrCreateResumeIdempotencyKey(runningBlock.id);

    timeBlockService.resume(runningBlock.id, idempotencyKey).subscribe({
      next: (response) => {
        this.applyRunningTimeBlockUpdate(response);
        this.refresh({ silent: true, onSettled: () => {
          this.resumeMutationState.set(null);
          this.blockMutationSubmitting.set(false);
        }}).subscribe();
      },
      error: (err: unknown) => {
        this.refreshDayOnFinalizeMutationError(err);
        this.blockMutationError.set(this.describeTimeBlockMutationError(err, 'resume'));
        this.blockMutationSubmitting.set(false);
      }
    });
  }

  resumePausedBlock(activityTypeId: string): void {
    const currentDay = this.day();
    const pausedBlock = this.getPausedBlockForActivity(activityTypeId);
    const timeBlockService = this.timeBlockService;
    if (!currentDay || !timeBlockService || !pausedBlock || currentDay.runningBlock || this.blockMutationSubmitting()) return;

    this.blockMutationSubmitting.set(true);
    this.blockMutationError.set(null);
    const idempotencyKey = this.getOrCreateResumeIdempotencyKey(pausedBlock.id);

    timeBlockService.resume(pausedBlock.id, idempotencyKey).subscribe({
      next: (response) => {
        this.applyRunningTimeBlockUpdate(response);
        this.refresh({ silent: true, onSettled: () => {
          this.resumeMutationState.set(null);
          this.blockMutationSubmitting.set(false);
        }}).subscribe();
      },
      error: (err: unknown) => {
        this.refreshDayOnFinalizeMutationError(err);
        this.blockMutationError.set(this.describeTimeBlockMutationError(err, 'resume'));
        this.blockMutationSubmitting.set(false);
      }
    });
  }

  saveAllocations(allocationDraft: Record<string, number>): void {
    const currentDay = this.day();
    if (!currentDay || this.allocationSaving()) return;

    const minutesPerSlot = this.resolveMinutesPerSlot(currentDay);
    if (minutesPerSlot === null) {
      this.allocationSaveError.set('Cannot save allocations yet: default slot minutes are still loading. Please retry.');
      return;
    }

    this.allocationSaving.set(true);
    this.allocationSaveError.set(null);
    this.allocationSaveSuccess.set(null);

    const request: PutDayAllocationsRequest = {
      allocations: this.buildAllocationInputs(minutesPerSlot, allocationDraft),
      version: currentDay.allocationsVersion
    };

    this.dayService.putAllocations(currentDay.dayDate, request).subscribe({
      next: (response) => {
        this.day.update((current) => current
          ? { ...current, allocationsVersion: response.version }
          : current);
        this.allocationSaveSuccess.set('Allocations saved.');
        if (response.lastDoneAt && response.lastDoneActivityTypeIds && response.lastDoneActivityTypeIds.length > 0) {
          const completedActivityIds = new Set(response.lastDoneActivityTypeIds);
          this.activityTypes.update((current) => current.map((activity) => (
            completedActivityIds.has(activity.id)
              ? { ...activity, lastDoneAt: response.lastDoneAt }
              : activity
          )));
        }
        if (response.completedCycle) {
          this.applyCycleLog({
            lastDoneAt: response.lastDoneAt ?? null,
            completedCycle: response.completedCycle ?? null
          });
        }
        this.refresh({ silent: true, onSettled: () => this.allocationSaving.set(false) }).subscribe();
      },
      error: (err: unknown) => {
        this.allocationSaveError.set(this.describeAllocationSaveError(err));
        if (err instanceof HttpErrorResponse && err.status === 412) {
          this.refresh({ silent: true, onSettled: () => this.allocationSaving.set(false) }).subscribe();
          return;
        }
        this.allocationSaving.set(false);
      }
    });
  }

  moveToHidden(activityTypeId: string, hidden: boolean): void {
    const currentDay = this.day();
    if (!currentDay) {
      return;
    }
    this.allocationSaveError.set(null);
    this.allocationSaveSuccess.set(null);
    const previousHiddenIds = new Set(this.hiddenActivityIds());
    const optimisticHiddenIds = new Set(previousHiddenIds);
    if (hidden) {
      optimisticHiddenIds.add(activityTypeId);
    } else {
      optimisticHiddenIds.delete(activityTypeId);
    }
    this.hiddenActivityIds.set(optimisticHiddenIds);
    this.dayService.patchAllocationHidden(currentDay.dayDate, activityTypeId, hidden).subscribe({
      next: (nextDay) => {
        this.hiddenActivityIds.set(this.resolveHiddenActivityIds(nextDay));
        this.day.set(nextDay);
      },
      error: (err: unknown) => {
        this.hiddenActivityIds.set(previousHiddenIds);
        this.allocationSaveError.set(describeGenericApiError(err, 'Failed to update hidden state'));
      }
    });
  }

  reorderGrouping(enabledOrdinals: ScheduleType[]): void {
    const meService = this.meService;
    if (!meService) {
      return;
    }
    if (this.reorderSubmitting()) {
      return;
    }
    const previousGroups = this.scheduleGroups();
    const scheduleGroupsUpdate = buildScheduleGroupPositionUpdates(enabledOrdinals, previousGroups);
    if (scheduleGroupsUpdate.length === 0) {
      return;
    }

    this.scheduleGroups.set(previousGroups.map((group) => {
      const update = scheduleGroupsUpdate.find((candidate) => candidate.ordinal === group.ordinal);
      if (!update || update.position == null) {
        return group;
      }
      return { ...group, position: update.position };
    }));
    this.reorderSubmitting.set(true);
    this.dayError.set(null);
    meService.updateSettings({ scheduleGroups: scheduleGroupsUpdate }).subscribe({
      next: (response) => {
        this.applyCycleSettings(response.settings);
        this.scheduleGroups.set(this.normalizeScheduleGroups(
          response.settings?.scheduleGroups ?? [],
          response.settings?.scheduleGroupCount
        ));
        this.reorderSubmitting.set(false);
      },
      error: (err: unknown) => {
        this.scheduleGroups.set(previousGroups);
        this.dayError.set(describeGenericApiError(err, 'Failed to reorder groupings'));
        this.reorderSubmitting.set(false);
      }
    });
  }

  persistDayStatusOldestFirst(next: boolean): void {
    const meService = this.meService;
    if (!meService) {
      return;
    }
    const previous = this.dayStatusOldestFirst();
    if (previous === next) {
      return;
    }
    this.dayStatusOldestFirst.set(next);
    meService.updateSettings({ dayStatusOldestFirst: next }).subscribe({
      next: (response) => {
        if (typeof response.settings?.dayStatusOldestFirst === 'boolean') {
          this.dayStatusOldestFirst.set(response.settings.dayStatusOldestFirst);
        }
      },
      error: () => {
        this.dayStatusOldestFirst.set(previous);
      }
    });
  }

  /**
   * Asks before a set-aside that would complete the activity's cycle, because the cycle resets
   * straight away and the set-aside can no longer be undone. Returns true when it is fine to go ahead.
   */
  confirmNotThisCycle(activityTypeId: string): boolean {
    const section = findCycleCompletedBySettingAside(this.dayStatusCycles(), activityTypeId);
    if (!section) {
      return true;
    }
    return this.confirmDialog.confirm(
      `Setting this aside completes the ${section.label} cycle and starts a new one. This can't be undone. Continue?`
    );
  }

  markNotThisCycle(activityTypeId: string): void {
    this.setNotThisCycleSubmitting(activityTypeId, true);
    this.notThisCycleError.set(null);
    this.activityTypeService.markNotThisCycle(activityTypeId).subscribe({
      next: () => {
        this.reloadAfterCycleChange();
        this.setNotThisCycleSubmitting(activityTypeId, false);
      },
      error: (err: unknown) => {
        this.notThisCycleError.set(describeGenericApiError(err, 'Failed to set activity aside for this cycle'));
        this.setNotThisCycleSubmitting(activityTypeId, false);
      }
    });
  }

  clearNotThisCycle(activityTypeId: string): void {
    this.setNotThisCycleSubmitting(activityTypeId, true);
    this.notThisCycleError.set(null);
    this.activityTypeService.clearNotThisCycle(activityTypeId).subscribe({
      next: () => {
        this.reloadAfterCycleChange();
        this.setNotThisCycleSubmitting(activityTypeId, false);
      },
      error: (err: unknown) => {
        this.notThisCycleError.set(describeGenericApiError(err, 'Failed to undo set aside'));
        this.setNotThisCycleSubmitting(activityTypeId, false);
      }
    });
  }

  isNotThisCycleSubmitting(activityTypeId: string): boolean {
    return !!this.notThisCycleSubmitting()[activityTypeId];
  }

  persistActivityOrder(
    orderedIds: string[],
    options: { kind: ActivityTypeKind; scheduleType?: ScheduleType },
    fallbackMessage: string
  ): void {
    if (this.activityReorderSubmitting()) {
      return;
    }
    const previous = this.activityTypes();
    const nextOrderById = new Map(orderedIds.map((id, index) => [id, index]));
    this.activityTypes.set(previous.map((activity) => {
      const nextOrder = nextOrderById.get(activity.id);
      return nextOrder === undefined ? activity : { ...activity, displayOrder: nextOrder };
    }));
    this.activityReorderSubmitting.set(true);
    this.activityReorderError.set(null);
    this.activityTypeService.reorder(orderedIds, options).subscribe({
      next: (updated) => {
        const byId = new Map(updated.map((activity) => [activity.id, activity]));
        this.activityTypes.update((current) => current.map((activity) => byId.get(activity.id) ?? activity));
        this.activityReorderSubmitting.set(false);
      },
      error: (err: unknown) => {
        this.activityTypes.set(previous);
        this.activityReorderError.set(describeGenericApiError(err, fallbackMessage));
        this.activityReorderSubmitting.set(false);
      }
    });
  }

  getSortedTimeBlockActivitiesForScheduleType(scheduleType: ScheduleType): ActivityType[] {
    return this.activityTypes()
      .filter((activity) => activity.kind === 'TIME_BLOCK' && activity.scheduleType === scheduleType)
      .sort((left, right) => left.displayOrder - right.displayOrder);
  }

  getAllocationRowsForScheduleType(scheduleType: ScheduleType): AllocationRow[] {
    const currentDay = this.day();
    const group = this.scheduleGroups().find((candidate) => candidate.ordinal === scheduleType);
    const cycleStartedAt = resolveScheduleGroupCycle(group) === 'LIFE'
      ? this.lifeCycleStartedAt()
      : this.workCycleStartedAt();
    return this.getSortedTimeBlockActivitiesForScheduleType(scheduleType).map((activity) => {
      const currentAllocation = currentDay?.allocations.find(
        (allocation) => allocation.activityTypeId === activity.id
      );
      const minutesPerSlot = currentAllocation?.activityDurationMinutes ?? currentAllocation?.minutesPerSlot ?? 0;
      const completedSlots = currentAllocation?.completedSlots ?? 0;
      const focusedTaskCount = currentAllocation?.focusedTaskCount ?? 0;
      const prioritizedTaskCount = currentAllocation?.prioritizedTaskCount ?? 0;
      const daysSince = computeDaysSince(activity.lastDoneAt, this.nowMs(), this.timezone());
      return {
        activityTypeId: activity.id,
        name: activity.name,
        completedSlots,
        currentSlots: currentAllocation?.slotCount ?? 0,
        isCompleted: (currentAllocation?.slotCount ?? 0) > 0
          && (currentAllocation?.completedSlots ?? 0) >= (currentAllocation?.slotCount ?? 0),
        startDurationMinutes: currentDay ? this.resolveStartDurationForActivity(activity.id) : null,
        category: activity.category,
        scheduleType,
        loggedMinutes: completedSlots * minutesPerSlot,
        isHidden: this.hiddenActivityIds().has(activity.id),
        focusedTaskCount,
        prioritizedTaskCount,
        hasFocusToday: focusedTaskCount > 0,
        hasPriorityToday: prioritizedTaskCount > 0,
        daysSince,
        daysSinceLabel: formatDaysSince(daysSince),
        doneThisCycle: isDoneThisCycle(activity.lastDoneAt, cycleStartedAt),
        notThisCycle: isDoneThisCycle(activity.notThisCycleAt, cycleStartedAt)
      };
    });
  }

  getPausedBlockForActivity(activityTypeId: string): DayBlock | null {
    return this.day()?.pausedBlocks?.find((block) => block.activityTypeId === activityTypeId) ?? null;
  }

  canStartBlock(activityTypeId: string): boolean {
    const currentDay = this.day();
    if (!currentDay || currentDay.runningBlock || this.dayLoading() || this.hasAnyStartSubmitting()) {
      return false;
    }
    if (this.getPausedBlockForActivity(activityTypeId)) {
      return false;
    }
    const duration = this.resolveStartDurationForActivity(activityTypeId);
    return duration !== null && duration > 0;
  }

  canResumePausedBlock(activityTypeId: string): boolean {
    const currentDay = this.day();
    if (!currentDay || currentDay.runningBlock || this.blockMutationSubmitting()) {
      return false;
    }
    return !!this.getPausedBlockForActivity(activityTypeId);
  }

  getActivityName(activityTypeId: string): string {
    return this.activityTypes().find((activity) => activity.id === activityTypeId)?.name ?? activityTypeId;
  }

  getRunningAllocationProgressLabel(): string | null {
    const currentDay = this.day();
    const runningBlock = currentDay?.runningBlock;
    if (!currentDay || !runningBlock) {
      return null;
    }

    const allocation = currentDay.allocations.find(
      (entry) => entry.activityTypeId === runningBlock.activityTypeId
    );
    if (!allocation || allocation.slotCount <= 0) {
      return null;
    }

    const slotIndex = Math.min(allocation.slotCount, allocation.completedSlots + 1);
    return `${slotIndex} / ${allocation.slotCount}`;
  }

  getRunningAllocationRemainingSlots(): number | null {
    const currentDay = this.day();
    const runningBlock = currentDay?.runningBlock;
    if (!currentDay || !runningBlock) {
      return null;
    }

    const allocation = currentDay.allocations.find(
      (entry) => entry.activityTypeId === runningBlock.activityTypeId
    );
    if (!allocation || allocation.slotCount <= 0) {
      return null;
    }

    return Math.max(0, allocation.slotCount - (allocation.completedSlots + 1));
  }

  private loadActivityTypes$(): Observable<boolean> {
    this.loading.set(true);
    return this.activityTypeService.list().pipe(
      tap((data) => {
        this.activityTypes.set(data);
      }),
      mapTo(true),
      catchError((err: unknown) => {
        this.error.set(describeGenericApiError(err, 'Failed to load activity types'));
        return of(false);
      }),
      finalize(() => {
        this.loading.set(false);
      })
    );
  }

  private loadDefaultSlotMinutes$(): Observable<boolean> {
    const meService = this.meService;
    if (!meService) {
      this.effectiveDefaultSlotMinutes.set(40);
      this.defaultSlotMinutesError.set(null);
      this.defaultSlotMinutesLoading.set(false);
      return of(true);
    }
    this.defaultSlotMinutesLoading.set(true);
    this.defaultSlotMinutesError.set(null);
    return meService.getMe().pipe(
      tap((response) => {
        this.effectiveDefaultSlotMinutes.set(response.settings?.defaultSlotMinutes ?? 40);
        this.applyCycleSettings(response.settings);
        this.scheduleGroups.set(this.normalizeScheduleGroups(
          response.settings?.scheduleGroups ?? [],
          response.settings?.scheduleGroupCount
        ));
      }),
      mapTo(true),
      catchError(() => {
        this.effectiveDefaultSlotMinutes.set(null);
        this.defaultSlotMinutesError.set('Failed to load default slot minutes. Retry to enable saving allocations for empty days.');
        return of(false);
      }),
      finalize(() => {
        this.defaultSlotMinutesLoading.set(false);
      })
    );
  }

  private loadToday$(options?: { silent?: boolean }): Observable<boolean> {
    if (!options?.silent) {
      this.dayLoading.set(true);
    }
    this.dayError.set(null);
    return this.dayService.getToday().pipe(
      tap((data) => {
        this.hiddenActivityIds.set(this.resolveHiddenActivityIds(data));
        this.day.set(data);
        this.nowMs.set(Date.now());
      }),
      mapTo(true),
      catchError((err: unknown) => {
        this.dayError.set(this.describeDayLoadError(err));
        return of(false);
      }),
      finalize(() => {
        if (!options?.silent) {
          this.dayLoading.set(false);
        }
      })
    );
  }

  private setNotThisCycleSubmitting(activityTypeId: string, value: boolean): void {
    this.notThisCycleSubmitting.update((current) => ({ ...current, [activityTypeId]: value }));
  }

  /** Reloads activity types and settings, since marking or clearing a set-aside may have reset a cycle. */
  reloadAfterCycleChange(): void {
    this.loadActivityTypes$().subscribe();
    this.loadDefaultSlotMinutes$().subscribe();
  }

  private setHabitSubmitting(activityTypeId: string, value: boolean): void {
    this.habitSubmitting.update((current) => ({ ...current, [activityTypeId]: value }));
  }

  private setStartSubmitting(activityTypeId: string, value: boolean): void {
    this.startSubmitting.update((current) => ({ ...current, [activityTypeId]: value }));
  }

  private hasAnyStartSubmitting(): boolean {
    return Object.values(this.startSubmitting()).some((isSubmitting) => isSubmitting);
  }

  private getOrCreateStartIdempotencyKey(activityTypeId: string): string {
    const existing = this.startIdempotencyKeys()[activityTypeId];
    if (existing) {
      return existing;
    }
    const key = generateIdempotencyKey();
    this.startIdempotencyKeys.update((current) => ({ ...current, [activityTypeId]: key }));
    return key;
  }

  private clearStartIdempotencyKey(activityTypeId: string): void {
    this.startIdempotencyKeys.update((current) => {
      if (!(activityTypeId in current)) return current;
      const { [activityTypeId]: _removed, ...rest } = current;
      return rest;
    });
  }

  private getOrCreateCompleteIdempotencyKey(blockId: string): string {
    const state = this.completeMutationState();
    if (state && state.blockId === blockId) {
      return state.idempotencyKey;
    }
    const idempotencyKey = generateIdempotencyKey();
    this.completeMutationState.set({ blockId, idempotencyKey });
    return idempotencyKey;
  }

  private getOrCreateSkipIdempotencyKey(blockId: string): string {
    const state = this.skipMutationState();
    if (state && state.blockId === blockId) {
      return state.idempotencyKey;
    }
    const idempotencyKey = generateIdempotencyKey();
    this.skipMutationState.set({ blockId, idempotencyKey });
    return idempotencyKey;
  }

  private getOrCreatePauseIdempotencyKey(blockId: string): string {
    const state = this.pauseMutationState();
    if (state && state.blockId === blockId) {
      return state.idempotencyKey;
    }
    const idempotencyKey = generateIdempotencyKey();
    this.pauseMutationState.set({ blockId, idempotencyKey });
    return idempotencyKey;
  }

  private getOrCreateResumeIdempotencyKey(blockId: string): string {
    const state = this.resumeMutationState();
    if (state && state.blockId === blockId) {
      return state.idempotencyKey;
    }
    const idempotencyKey = generateIdempotencyKey();
    this.resumeMutationState.set({ blockId, idempotencyKey });
    return idempotencyKey;
  }

  private resolveHiddenActivityIds(day: DayViewResponse): Set<string> {
    return new Set(
      day.allocations
        .filter((allocation) => allocation.hiddenToday)
        .map((allocation) => allocation.activityTypeId)
    );
  }

  private resolveMinutesPerSlot(currentDay: DayViewResponse): number | null {
    const fromDay = currentDay.allocations[0]?.minutesPerSlot;
    if (fromDay !== undefined) return fromDay;
    return this.effectiveDefaultSlotMinutes();
  }

  private resolveStartDurationForActivity(activityTypeId: string): number | null {
    const fromActivityDefault = this.activityTypes().find(
      (activity) => activity.id === activityTypeId
    )?.defaultMinutes;
    if (fromActivityDefault !== undefined && fromActivityDefault !== null) return fromActivityDefault;

    return this.effectiveDefaultSlotMinutes();
  }

  private resolveStartSourceForActivity(activityTypeId: string, currentDay: DayViewResponse): TimeBlockSource {
    const existingAllocation = currentDay.allocations.find(
      (allocation) => allocation.activityTypeId === activityTypeId
    );
    if (existingAllocation && existingAllocation.slotCount > 0) {
      return 'ALLOCATED';
    }
    return 'ADHOC';
  }

  private buildAllocationInputs(minutesPerSlot: number, allocationDraft: Record<string, number>): PutDayAllocationInput[] {
    const timeBlockIds = new Set(
      this.activityTypes()
        .filter((activity) => activity.kind === 'TIME_BLOCK')
        .map((activity) => activity.id)
    );

    return Object.entries(allocationDraft)
      .filter(([activityTypeId, slotCount]) => timeBlockIds.has(activityTypeId) && slotCount > 0)
      .map(([activityTypeId, slotCount]) => ({
        activityTypeId,
        slotCount,
        minutesPerSlot
      }));
  }

  private applyHabitIncrement(response: HabitIncrementResponse): void {
    this.day.update((current) => {
      if (!current || current.dayDate !== response.dayDate) return current;
      const existingIndex = current.habits.findIndex((habit) => habit.activityTypeId === response.activityTypeId);
      let nextHabits: DayHabit[];
      if (existingIndex === -1) {
        nextHabits = [...current.habits, {
          activityTypeId: response.activityTypeId,
          total: response.total,
          target: null,
          openTaskCount: 0
        }];
      } else {
        nextHabits = current.habits.map((habit, index) => index === existingIndex
          ? { ...habit, total: response.total }
          : habit);
      }
      return { ...current, habits: nextHabits };
    });
    if (response.lastDoneAt) {
      this.applyCycleLog({
        activityTypeId: response.activityTypeId,
        lastDoneAt: response.lastDoneAt
      });
    }
  }

  private applyFinalizedTimeBlock(response: TimeBlockResponse): void {
    this.day.update((current) => {
      const runningBlock = current?.runningBlock;
      if (!current || !runningBlock || runningBlock.id !== response.id) {
        return current;
      }

      const finalizedBlock: DayBlock = {
        id: response.id,
        activityTypeId: response.activityTypeId,
        startedAt: response.startedAt,
        endedAt: response.endedAt,
        pausedAt: response.pausedAt,
        accumulatedPauseMs: response.accumulatedPauseMs,
        durationMinutes: response.durationMinutes,
        status: response.status,
        skipReason: response.skipReason
      };

      const hasExistingBlock = current.blocks.some((block) => block.id === finalizedBlock.id);
      const nextBlocks = hasExistingBlock
        ? current.blocks.map((block) => block.id === finalizedBlock.id ? finalizedBlock : block)
        : [...current.blocks, finalizedBlock];

      const nextAllocations = response.status === 'COMPLETED'
        ? current.allocations.map((allocation) => allocation.activityTypeId === response.activityTypeId
          ? { ...allocation, completedSlots: allocation.completedSlots + 1 }
          : allocation)
        : current.allocations;

      return {
        ...current,
        runningBlock: null,
        blocks: nextBlocks,
        allocations: nextAllocations
      };
    });
    if (response.status === 'COMPLETED') {
      this.applyCycleLog(response);
    }
  }

  private applyRunningTimeBlockUpdate(response: TimeBlockResponse): void {
    this.day.update((current) => {
      if (!current) {
        return current;
      }

      const mappedBlock: DayBlock = this.toDayBlock(response);
      const pausedBlocks = current.pausedBlocks ?? [];
      const pausedBlockIndex = pausedBlocks.findIndex((block) => block.id === response.id);

      if (response.status === 'PAUSED') {
        const runningBlock = current.runningBlock;
        if (!runningBlock || runningBlock.id !== response.id) {
          return current;
        }
        const nextPausedBlocks = pausedBlockIndex >= 0
          ? pausedBlocks.map((block) => block.id === response.id ? mappedBlock : block)
          : [...pausedBlocks, mappedBlock];
        return {
          ...current,
          runningBlock: null,
          pausedBlocks: nextPausedBlocks
        };
      }

      if (response.status !== 'RUNNING') {
        return current;
      }

      const runningBlock = current.runningBlock;
      if (runningBlock && runningBlock.id === response.id) {
        return {
          ...current,
          runningBlock: mappedBlock,
          pausedBlocks: pausedBlocks.filter((block) => block.id !== response.id)
        };
      }

      if (pausedBlockIndex < 0) {
        return current;
      }

      return {
        ...current,
        runningBlock: mappedBlock,
        pausedBlocks: pausedBlocks.filter((block) => block.id !== response.id)
      };
    });
  }

  private toDayBlock(response: TimeBlockResponse): DayBlock {
    return {
      id: response.id,
      activityTypeId: response.activityTypeId,
      startedAt: response.startedAt,
      endedAt: response.endedAt,
      pausedAt: response.pausedAt,
      accumulatedPauseMs: response.accumulatedPauseMs,
      durationMinutes: response.durationMinutes,
      status: response.status,
      skipReason: response.skipReason
    };
  }

  private refreshDayOnFinalizeMutationError(err: unknown): void {
    if (!(err instanceof HttpErrorResponse)) {
      return;
    }
    if (err.status === 404 || err.status === 409) {
      this.refresh().subscribe();
    }
  }

  private startNowMsTicker(): void {
    this.stopNowMsTicker();
    this.nowMsIntervalId = setInterval(() => {
      this.nowMs.set(Date.now());
    }, 60_000);
  }

  private stopNowMsTicker(): void {
    if (this.nowMsIntervalId !== null) {
      clearInterval(this.nowMsIntervalId);
      this.nowMsIntervalId = null;
    }
  }

  private applyCycleSettings(settings: {
    timezone?: string;
    scheduleGroups?: ScheduleGroup[];
    workCycleStartedAt?: string;
    lifeCycleStartedAt?: string;
    workCycleLabel?: string | null;
    lifeCycleLabel?: string | null;
    workCycleCompletedCount?: number;
    lifeCycleCompletedCount?: number;
    lastCompletedWorkCycle?: LastCompletedCycleSummary;
    lastCompletedLifeCycle?: LastCompletedCycleSummary;
    dayStatusOldestFirst?: boolean;
  } | undefined): void {
    this.timezone.set(settings?.timezone?.trim() || 'UTC');
    this.workCycleStartedAt.set(settings?.workCycleStartedAt ?? null);
    this.lifeCycleStartedAt.set(settings?.lifeCycleStartedAt ?? null);
    this.workCycleLabel.set(settings?.workCycleLabel ?? null);
    this.lifeCycleLabel.set(settings?.lifeCycleLabel ?? null);
    this.workCycleCompletedCount.set(Math.max(0, Math.floor(settings?.workCycleCompletedCount ?? 0)));
    this.lifeCycleCompletedCount.set(Math.max(0, Math.floor(settings?.lifeCycleCompletedCount ?? 0)));
    this.lastCompletedWorkCycle.set(settings?.lastCompletedWorkCycle ?? null);
    this.lastCompletedLifeCycle.set(settings?.lastCompletedLifeCycle ?? null);
    if (typeof settings?.dayStatusOldestFirst === 'boolean') {
      this.dayStatusOldestFirst.set(settings.dayStatusOldestFirst);
    }
  }

  private applyCycleLog(response: {
    activityTypeId?: string;
    lastDoneAt?: string | null;
    completedCycle?: DayStatusCycleKind | null;
  }): void {
    const lastDoneAt = response.lastDoneAt ?? null;
    if (lastDoneAt && response.activityTypeId) {
      this.activityTypes.update((current) => current.map((activity) => (
        activity.id === response.activityTypeId
          ? { ...activity, lastDoneAt }
          : activity
      )));
    }
    if (response.completedCycle === 'WORK' && lastDoneAt) {
      const summary = this.buildLastCompletedSummary(
        this.workCycleCompletedCount() + 1,
        this.workCycleStartedAt(),
        lastDoneAt
      );
      this.workCycleCompletedCount.update((current) => current + 1);
      if (summary) {
        this.lastCompletedWorkCycle.set(summary);
      }
      this.workCycleStartedAt.set(lastDoneAt);
      this.showCelebration('WORK', summary);
    } else if (response.completedCycle === 'LIFE' && lastDoneAt) {
      const summary = this.buildLastCompletedSummary(
        this.lifeCycleCompletedCount() + 1,
        this.lifeCycleStartedAt(),
        lastDoneAt
      );
      this.lifeCycleCompletedCount.update((current) => current + 1);
      if (summary) {
        this.lastCompletedLifeCycle.set(summary);
      }
      this.lifeCycleStartedAt.set(lastDoneAt);
      this.showCelebration('LIFE', summary);
    }
  }

  private showCelebration(cycle: DayStatusCycleKind, summary: LastCompletedCycleSummary | null): void {
    this.celebrationCycle.set(cycle);
    this.celebrationSummary.set(summary);
    if (this.celebrationTimeoutId !== null) {
      clearTimeout(this.celebrationTimeoutId);
    }
    this.celebrationTimeoutId = setTimeout(() => {
      this.celebrationCycle.set(null);
      this.celebrationSummary.set(null);
      this.celebrationTimeoutId = null;
    }, 4000);
  }

  private buildLastCompletedSummary(
    number: number,
    startedAt: string | null,
    completedAt: string | null
  ): LastCompletedCycleSummary | null {
    if (!completedAt || !Number.isFinite(number) || number <= 0) {
      return null;
    }
    const durationDays = computeCycleDurationDays(startedAt, completedAt, this.timezone()) ?? 0;
    return {
      number,
      durationDays: Math.max(0, durationDays)
    };
  }

  private defaultScheduleGroups(): ScheduleGroup[] {
    return SCHEDULE_GROUP_ORDER.map((ordinal, index) => ({
      ordinal,
      label: null,
      targetEndLocalTime: null,
      budgetMinutes: null,
      position: index,
      cycle: 'WORK',
      enabled: index < 2
    }));
  }

  private normalizeScheduleGroups(rawGroups: ScheduleGroup[], groupCount?: number): ScheduleGroup[] {
    const enabledCount = Number.isFinite(groupCount) ? Math.max(1, Math.min(5, Number(groupCount))) : 2;
    return SCHEDULE_GROUP_ORDER.map((ordinal, index) => {
      const existing = rawGroups.find((group) => group.ordinal === ordinal);
      return {
        ordinal,
        label: existing?.label ?? null,
        targetEndLocalTime: existing?.targetEndLocalTime ?? null,
        budgetMinutes: existing?.budgetMinutes ?? null,
        cycle: existing?.cycle === 'LIFE' ? 'LIFE' : 'WORK',
        position: this.normalizeScheduleGroupPosition(existing?.position, index),
        enabled: existing?.enabled ?? index < enabledCount
      };
    });
  }

  private normalizeScheduleGroupPosition(position: number | null | undefined, fallback: number): number {
    if (!Number.isFinite(position)) {
      return fallback;
    }
    return Math.max(0, Math.min(4, Math.floor(Number(position))));
  }

  private describeDayLoadError(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const serverMsg = extractApiErrorMessage(err);
      if (serverMsg) {
        return serverMsg;
      }
      if (err.status === 404) {
        return 'Day view not found.';
      }
      if (err.status === 401 || err.status === 403) {
        return 'Not authorized to load day view. Try signing in again.';
      }
      return `Failed to load day view (HTTP ${err.status}).`;
    }
    return 'Failed to load day view';
  }

  private describeAllocationSaveError(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const serverMsg = extractApiErrorMessage(err);
      if (err.status === 412) {
        return serverMsg ?? 'Allocations changed elsewhere. Reloaded latest values, please review and save again.';
      }
      if (serverMsg) {
        return serverMsg;
      }
      if (err.status === 400) {
        return 'Invalid allocation input. Please review the form and try again.';
      }
      if (err.status === 401 || err.status === 403) {
        return 'Not authorized to save allocations. Try signing in again.';
      }
      if (err.status === 0) {
        return 'Could not reach the server. Check your connection and try again.';
      }
      return `Failed to save allocations (HTTP ${err.status}).`;
    }
    return 'Failed to save allocations';
  }

  private describeTimeBlockMutationError(err: unknown, action: 'start' | 'pause' | 'resume' | 'complete' | 'skip'): string {
    if (err instanceof HttpErrorResponse) {
      const serverMsg = extractApiErrorMessage(err);
      if (serverMsg) {
        return serverMsg;
      }
      if (err.status === 400) {
        return `Invalid ${action} request. Please retry.`;
      }
      if (err.status === 401 || err.status === 403) {
        return `Not authorized to ${action} this block. Try signing in again.`;
      }
      if (err.status === 404) {
        return 'This block was not found. Refreshing your day view can help.';
      }
      if (err.status === 409) {
        return action === 'start'
          ? 'Another running block already exists. Refreshing your day view can help.'
          : action === 'pause'
            ? 'This block is already paused.'
            : action === 'resume'
              ? 'This block is not paused.'
              : 'This block was already finalized.';
      }
      if (err.status === 0) {
        return `Could not reach the server to ${action} this block. Check your connection and try again.`;
      }
      return `Failed to ${action} block (HTTP ${err.status}).`;
    }
    return `Failed to ${action} block`;
  }
}
